<template>
  <div ref="referenceElement" class="help-center-popover-component w-full">
    <SideNavItem
      capture-button-name="open-help-center"
      :label="t('help-center.side-nav-label')"
      :icon="IconName.QuestionCircle"
      :active="isPopoverOpen"
      :aria-controls="popoverId"
      aria-haspopup="dialog"
      :aria-expanded="isPopoverOpen"
      @click.stop="togglePopover"
    />

    <Popover
      v-bind="{ ariaLabel: t('help-center.popover.title') }"
      :id="popoverId"
      class="help-center-popover"
      :is-shown="isPopoverOpen"
      :popover-element-ref="setPopoverElement"
      :style="popoverStyle"
      @click.stop
    >
      <h2 class="typo-title4 shrink-0 tracking-[-0.02em] text-[#24202C]">{{ t('help-center.popover.title') }}</h2>

      <LineButton
        class="w-full shrink-0"
        :variant="LineButtonTypes.Primary"
        :color="LineButtonColor.Primary"
        :content="t('home.game-card.schedule-meeting')"
        :href="t('home.game-card.url')"
        target="_blank"
      />

      <LineButton
        class="w-full shrink-0"
        :variant="LineButtonTypes.Secondary"
        :color="secondaryActionState === 'success' ? LineButtonColor.Success : LineButtonColor.Primary"
        :content="
          secondaryActionState === 'success'
            ? t('help-center.popover.copied-email')
            : t('help-center.popover.copy-email')
        "
        type="button"
        @click="copyEmail"
      />

      <template v-if="helpUrl">
        <div class="h-px w-full shrink-0 bg-[#E8DFEA]" aria-hidden="true" />

        <SideNavItem
          class="shrink-0"
          :label="t('help-center.popover.footer-link')"
          :href="helpUrl"
          :icon="IconName.QuestionCircle"
          :outside-link="true"
          :active="false"
          @click="closePopover"
        />
      </template>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconName } from '@kiplin/web-common/icon';
import { LineButton, LineButtonColor, LineButtonTypes } from '@kiplin/web-common/design-system';
import { container } from '@shared/common/dependency-injection/domain/container';
import { HelpCenterDependencyTypes } from '@client/feature/help-center/help-center.dependency-types';
import Popover from '@design-system/common/application/component/Popover/Popover.vue';
import { usePopover } from '@design-system/common/application/composable/use-popover.composable';
import SideNavItem from '@design-system/side-nav/application/component/SideNavItem/SideNavItem.vue';

const { t } = useI18n();
const popoverId = `help-center-popover-${useId()}`;
const helpUrl = container.get<string>(HelpCenterDependencyTypes.HelpUrl);
const secondaryActionState = ref<'default' | 'success'>('default');
const { closePopover, isPopoverOpen, popoverStyle, referenceElement, setPopoverElement, togglePopover } = usePopover({
  placement: 'right',
  crossAxisAlignment: 'end',
  gap: 8,
  getGapElement: (element) => element.closest('nav'),
});

watch(isPopoverOpen, (isOpen) => {
  if (!isOpen) {
    secondaryActionState.value = 'default';
  }
});

async function copyEmail(): Promise<void> {
  try {
    await navigator.clipboard.writeText('supportclient@kiplin.com');
  } catch {
    return;
  }

  secondaryActionState.value = 'success';
}
</script>
